/**
 * Delivery Table Component
 * Displays list of deliveries in a clean table format
 * Shows actual delivery data from Commerce API
 */

import type { Delivery } from '@/modules/commerce/delivery/types/delivery';
import { getStageFromStatus } from '@/modules/commerce/delivery/types/delivery';
import { StatusBadge, StageIndicator } from './StatusBadge';

interface DeliveryTableProps {
  deliveries: Delivery[];
  loading?: boolean;
  onView?: (delivery: Delivery) => void;
  onEdit?: (delivery: Delivery) => void;
  onCancel?: (delivery: Delivery) => void;
}

// Priority badge colors
const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'bg-green-100 text-green-800',
  NORMAL: 'bg-blue-100 text-blue-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  HIGH: 'bg-orange-100 text-orange-800',
  URGENT: 'bg-red-100 text-red-800',
};

// Format date helper
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Format amount helper
const formatAmount = (amount?: number | null): string => {
  if (!amount) return '-';
  return `₹${amount.toLocaleString('en-IN')}`;
};

// Extract city from full address (get last meaningful part before pincode)
const extractCityFromAddress = (address?: string): string => {
  if (!address) return '';
  // Split by comma and find the city (usually 2nd to last part, before pincode/state)
  const parts = address.split(',').map(p => p.trim()).filter(p => p.length > 0);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  // Try to find city - usually before pincode
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    // Skip if it looks like a pincode (6 digits)
    if (/^\d{6}$/.test(part)) continue;
    // Skip if it looks like a state name
    if (/^(Maharashtra|Gujarat|Delhi|Karnataka|Tamil Nadu|Telangana|Andhra Pradesh|Rajasthan|West Bengal|Uttar Pradesh|India)$/i.test(part)) continue;
    return part;
  }
  return parts[0];
};

export function DeliveryTable({
  deliveries,
  loading = false,
  onView,
  onEdit,
  onCancel,
}: DeliveryTableProps) {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center gap-3">
          <svg className="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-lg text-gray-600">Loading deliveries...</span>
        </div>
      </div>
    );
  }

  if (deliveries.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-64 bg-white rounded-lg shadow">
        <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
        </svg>
        <div className="text-xl text-gray-600 mb-2">No deliveries found</div>
        <div className="text-gray-400">Try adjusting your filters or create a new delivery request</div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="min-w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Order ID</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Stage</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Delivery Address</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Driver</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Assigned From</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {deliveries.map((delivery, index) => {
            const stage = delivery.stage ?? getStageFromStatus(delivery.status);
            // Get delivery address - prefer dropAddress, then deliveryAddress
            const fullAddress = delivery.dropAddress || delivery.deliveryAddress || '';
            const displayAddress = fullAddress.length > 50 ? fullAddress.substring(0, 50) + '...' : fullAddress;

            return (
              <tr
                key={delivery.id || index}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onView?.(delivery)}
              >
                {/* Order ID - show requestNumber (TR-xxx format) like Transport module */}
                <td className="px-4 py-3">
                  <div className="min-w-0">
                    <span className="font-mono text-sm text-gray-700 font-medium">
                      {delivery.requestNumber || '-'}
                    </span>
                    {delivery.orderNumber && (
                      <p className="text-xs text-gray-500 truncate">
                        {delivery.orderNumber}
                      </p>
                    )}
                  </div>
                </td>

                {/* Stage Indicator */}
                <td className="px-4 py-3">
                  <StageIndicator stage={stage} />
                </td>

                {/* Status Badge */}
                <td className="px-4 py-3">
                  <StatusBadge status={delivery.status} />
                </td>

                {/* Customer Info */}
                <td className="px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate max-w-[150px]">
                      {delivery.customerName || delivery.dropContactName || delivery.contactPerson || '-'}
                    </p>
                    {(delivery.dropContactPhone || delivery.contactPhone) && (
                      <p className="text-xs text-gray-500 truncate">
                        {delivery.dropContactPhone || delivery.contactPhone}
                      </p>
                    )}
                  </div>
                </td>

                {/* Delivery Address */}
                <td className="px-4 py-3">
                  <div className="max-w-[200px]">
                    <p className="text-sm text-gray-700 truncate" title={fullAddress}>
                      {displayAddress || '-'}
                    </p>
                    {fullAddress && (
                      <p className="text-xs text-gray-500">
                        {extractCityFromAddress(fullAddress)}
                      </p>
                    )}
                  </div>
                </td>

                {/* Driver Info */}
                <td className="px-4 py-3">
                  {delivery.driverName ? (
                    <div>
                      <p className="text-sm font-medium">{delivery.driverName}</p>
                      {delivery.vehicleNumber && (
                        <p className="text-xs text-gray-500">{delivery.vehicleNumber}</p>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm italic">Unassigned</span>
                  )}
                </td>

                {/* Assigned From */}
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    delivery.assignedFrom === 'UrjaVyapar'
                      ? 'bg-purple-100 text-purple-800'
                      : delivery.assignedFrom === 'Transport'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {delivery.assignedFrom || 'UrjaVyapar'}
                  </span>
                </td>

                {/* Date */}
                <td className="px-4 py-3">
                  <span className="text-sm text-gray-600">
                    {formatDate(delivery.createdAt)}
                  </span>
                </td>

                {/* Actions */}
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center gap-1">
                    {onView && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(delivery);
                        }}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Details"
                        aria-label="View delivery details"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                    {onEdit && !['DELIVERED', 'COMPLETED', 'CANCELLED'].includes(delivery.status?.toUpperCase() || '') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(delivery);
                        }}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                        title="Edit Delivery"
                        aria-label="Edit delivery"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                    {onCancel && !['CANCELLED', 'COMPLETED'].includes(delivery.status?.toUpperCase() || '') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancel(delivery);
                        }}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        title="Cancel Delivery"
                        aria-label="Cancel delivery"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

